import {
  CalcHandCandidatePoolResult,
  FindModelResponse,
  Grasp2pInfo,
  HandResultItem,
  HandResultItemWithFingertip,
  Score,
  SUGGESTION_PATTERNS
} from "../types/pj1dbApiHandling";
import {CALCULATED_SORT_ORDER, CalculatedSortOrder} from "../constants/handselection";
import {AttachmentDocument} from "../features/equipmentdb/types/pj1dbApiAttachment";

export function pickGrasp2p(directionIndex: number, grasp2p: FindModelResponse['grasp2p']): {
  objectId: string,
  info: Grasp2pInfo
}[] {
  return Object
    .entries(grasp2p)
    .filter(([_, g]) => g[directionIndex] !== undefined)
    .map(([objectId, list]) => ({
      objectId,
      info: list[directionIndex]
    }));
}

export function getDirectionIndex(direction: Required<FindModelResponse>['gravityWithDirections'][number], directions: Required<FindModelResponse>['gravityWithDirections']): number {
  return directions.map(d => d.thumbnail).indexOf(direction.thumbnail);
}

export function eqDirection(a: Required<FindModelResponse>['gravityWithDirections'][number], b: Required<FindModelResponse>['gravityWithDirections'][number]): boolean {
  return a.thumbnail === b.thumbnail;
}

export function sortCalculationResults(results: CalcHandCandidatePoolResult['results'], sortOrder: CalculatedSortOrder): CalcHandCandidatePoolResult['results'] {
  const suggestionPatternPriorities = {
    [SUGGESTION_PATTERNS.HAND_PLUS_VIRTUAL_FINGERTIP]: 0,
    [SUGGESTION_PATTERNS.HAND_PLUS_FINGERTIP]: 1,
    [SUGGESTION_PATTERNS.HAND]: 2,
  } as const;
  const calcCompareSuggestionPatternDesc = (a: HandResultItem, b: HandResultItem) => suggestionPatternPriorities[b.suggestionPattern] - suggestionPatternPriorities[a.suggestionPattern];

  switch (sortOrder) {
    case CALCULATED_SORT_ORDER.SCORE_TOTAL:
    case CALCULATED_SORT_ORDER.SCORE_OPEN:
    case CALCULATED_SORT_ORDER.SCORE_FORCE:
    case CALCULATED_SORT_ORDER.SCORE_STROKE:
      // スコアが高い順
      return sortScoreable({list: results, key: sortOrder})
        .map(result => ({
          ...result,
          hands: sortScoreable({list: result.hands, key: sortOrder}),
        }));
    case CALCULATED_SORT_ORDER.SUGGESTION_PATTERN:
      return [...results].map(r => ({
        ...r,
        hands: r.hands.sort((a, b) => {
          const compare = calcCompareSuggestionPatternDesc(a, b);
          return compare === 0 ? b.score.total - a.score.total : compare;
        }),
      }));
    default:
      throw new Error('Unknown sort order');
  }
}

export function sortScoreable<T extends { score: Score }>(params: { list: T[], key: CalculatedSortOrder }) {
  const priorities = {
    [CALCULATED_SORT_ORDER.SCORE_TOTAL]: 'total',
    [CALCULATED_SORT_ORDER.SCORE_OPEN]: 'open',
    [CALCULATED_SORT_ORDER.SCORE_FORCE]: 'force',
    [CALCULATED_SORT_ORDER.SCORE_STROKE]: 'stroke',
    [CALCULATED_SORT_ORDER.SUGGESTION_PATTERN]: 'total',
  } as const satisfies Record<CalculatedSortOrder, keyof Score>;

  const key = priorities[params.key] ?? 'total';

  return [...params.list].sort((a, b) => b.score[key] - a.score[key]);
}

export function isConnectable(handId: string, armId: string, attachments: AttachmentDocument[]): boolean {
  return attachments.some(attachment =>
    attachment.attr.handIds.includes(handId) && attachment.attr.armIds.includes(armId)
  );
}

export function hasFingertipIdResult(result: HandResultItem): result is HandResultItemWithFingertip {
  return 'fingertipID' in result;
}
